import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getBookmarks, getUserPreferences, saveUserPreferences, clearCache } from '../api/client';
import { useAuth, useGoogleButton } from '../contexts/AuthContext';
import { logFileOpen } from '../utils/usageLogger';
import type { Bookmark } from '../types/bookmarks';
import { SkeletonLoader } from '../components/SkeletonLoader';

/**
 * Get icon based on URL type
 */
function getIconForUrl(url: string) {
    const lowerUrl = url.toLowerCase();

    // Google Sheets
    if (lowerUrl.includes('docs.google.com/spreadsheets')) {
        return (
            <div className="w-10 h-10 rounded bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-2h2v2zm0-4H7v-2h2v2zm0-4H7V7h2v2zm4 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2zm4 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2z" />
                </svg>
            </div>
        );
    }

    // Google Docs
    if (lowerUrl.includes('docs.google.com/document')) {
        return (
            <div className="w-10 h-10 rounded bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
                </svg>
            </div>
        );
    }

    // Google Slides
    if (lowerUrl.includes('docs.google.com/presentation')) {
        return (
            <div className="w-10 h-10 rounded bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center text-yellow-600 dark:text-yellow-400">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-2h2v2zm0-4H7v-2h2v2zm0-4H7V7h2v2zm4 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2zm4 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2z" />
                </svg>
            </div>
        );
    }

    // Default file/link
    return (
        <div className="w-10 h-10 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
        </div>
    );
}

function getDomainFromUrl(url: string) {
    try {
        return new URL(url).hostname;
    } catch {
        return url;
    }
}

export default function BookmarksPage() {
    const auth = useAuth();
    const { renderButton } = useGoogleButton();
    const googleButtonRef = useRef<HTMLDivElement>(null);

    const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
    const [originalBookmarks, setOriginalBookmarks] = useState<Bookmark[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedTeam, setSelectedTeam] = useState<string>('All');
    const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
    const [favoriteUrls, setFavoriteUrls] = useState<Set<string>>(new Set());
    const [theme, setTheme] = useState<'dark' | 'light'>('dark');
    const [isSaving, setIsSaving] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);

    // Initialize Google button if needed
    useEffect(() => {
        if (!auth.isAuthenticated && !auth.isLoading && googleButtonRef.current) {
            renderButton(googleButtonRef.current);
        }
    }, [auth.isAuthenticated, auth.isLoading, renderButton]);

    // Load Data & Preferences
    const loadData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            // Concurrently fetch bookmarks and preferences
            const [bookmarksResult, prefsResult] = await Promise.all([
                getBookmarks(),
                getUserPreferences()
            ]);

            if (bookmarksResult.success) {
                setOriginalBookmarks(bookmarksResult.bookmarks);
                setBookmarks(bookmarksResult.bookmarks);
                setDataLoaded(true);
            } else {
                setError(bookmarksResult.error || 'Failed to load bookmarks');
            }

            if (prefsResult.success && prefsResult.preferences) {
                if (prefsResult.preferences.favorites) {
                    setFavoriteUrls(new Set(prefsResult.preferences.favorites));
                }
                if (prefsResult.preferences.theme) {
                    setTheme(prefsResult.preferences.theme);
                }
            }

        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Effect to trigger data load when authenticated
    useEffect(() => {
        if (auth.isLoading) return;

        if (auth.isAuthenticated && !dataLoaded) {
            clearCache(); // Ensure we get fresh data with the token
            loadData();
        }
    }, [auth.isAuthenticated, auth.isLoading, dataLoaded, loadData]);

    // Save preferences helper
    const savePrefs = async (newFavorites: string[], newTheme: 'dark' | 'light') => {
        setIsSaving(true);
        try {
            await saveUserPreferences({
                favorites: newFavorites,
                theme: newTheme
            });
        } catch (err) {
            console.error('Failed to save preferences', err);
        } finally {
            setIsSaving(false);
        }
    };

    // Toggle Favorite
    const toggleFavorite = async (e: React.MouseEvent, url: string) => {
        e.preventDefault();
        e.stopPropagation();

        const newFavorites = new Set(favoriteUrls);
        if (newFavorites.has(url)) {
            newFavorites.delete(url);
        } else {
            newFavorites.add(url);
        }

        setFavoriteUrls(newFavorites);
        await savePrefs(Array.from(newFavorites), theme);
    };

    // Toggle Theme
    const toggleTheme = async () => {
        const newTheme = theme === 'dark' ? 'light' : 'dark';
        setTheme(newTheme);
        await savePrefs(Array.from(favoriteUrls), newTheme);
    };

    // Extract unique teams
    const teams = useMemo(() => {
        const teamSet = new Set(originalBookmarks.map(b => b.team || 'General'));
        return Array.from(teamSet).sort();
    }, [originalBookmarks]);

    // Filter bookmarks
    const filteredBookmarks = useMemo(() => {
        let result = [...bookmarks];

        if (showFavoritesOnly) {
            result = result.filter(b => favoriteUrls.has(b.url));
        }

        if (selectedTeam !== 'All') {
            result = result.filter(b => (b.team || 'General') === selectedTeam);
        }

        return result;
    }, [bookmarks, selectedTeam, showFavoritesOnly, favoriteUrls]);

    // Auth screen if not authenticated
    if (auth.isLoading) {
        return (
            <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
                <SkeletonLoader className="h-12 w-12 rounded-full" />
            </div>
        );
    }

    if (!auth.isAuthenticated) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Bookmarks</h1>
                    <p className="text-gray-600 dark:text-gray-400 mb-8">Sign in with your SSD account to access resources</p>
                    <div className="flex justify-center mb-6">
                        <div ref={googleButtonRef} className="min-w-[200px]" />
                    </div>
                    {auth.error && <p className="text-red-500 text-sm mt-4">{auth.error}</p>}
                    <Link to="/" className="inline-block mt-8 text-blue-600 dark:text-blue-400 font-medium hover:underline">
                        Back to Home
                    </Link>
                </div>
            </div>
        );
    }


    if (isLoading && !dataLoaded) {
        return (
            <div className="flex h-[calc(100vh-64px)] overflow-hidden">
                {/* Sidebar Loading */}
                <div className="w-64 border-r border-gray-200 dark:border-gray-800 p-6 hidden md:block">
                    <SkeletonLoader className="h-8 w-3/4 mb-6" />
                    <div className="space-y-4">
                        <SkeletonLoader className="h-4 w-full" />
                        <SkeletonLoader className="h-4 w-full" />
                        <SkeletonLoader className="h-4 w-full" />
                    </div>
                </div>
                {/* Main Content Loading */}
                <div className="flex-1 p-8 overflow-y-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3, 4, 5, 6].map(i => (
                            <SkeletonLoader key={i} className="h-32 rounded-xl" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 max-w-7xl mx-auto">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-red-600 dark:text-red-400 font-medium">Error loading bookmarks:</p>
                    <p className="text-red-500 dark:text-red-300">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className={theme === 'dark' ? 'dark' : ''}>
            <div className="flex bg-gray-50 dark:bg-gray-900 h-full min-h-screen text-gray-900 dark:text-gray-100">
                {/* Sidebar */}
                <aside className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex-shrink-0 hidden md:flex flex-col">
                    {/* Back Button */}
                    <div className="px-4 py-6 border-b border-gray-200 dark:border-gray-700">
                        <Link
                            to="/"
                            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            <span className="font-medium">Back to Home</span>
                        </Link>
                    </div>

                    <div className="p-6">
                        {/* Favorites Toggle */}
                        <div className="mb-6">
                            <button
                                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                                className={`w-full flex items-center px-3 py-2 text-sm font-semibold rounded-lg transition-colors ${showFavoritesOnly
                                    ? 'bg-[#3d2b1f] border border-[#a87a2d]/30 text-[#facc15]'
                                    : 'text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                            >
                                <span>Showing Favorites</span>
                                <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${showFavoritesOnly
                                    ? 'bg-gray-700/50 text-white'
                                    : 'text-gray-400 bg-gray-100 dark:bg-gray-800'
                                    }`}>
                                    {favoriteUrls.size}
                                </span>
                            </button>
                        </div>

                        <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                            Teams
                        </h2>
                        <nav className="space-y-1">
                            {/* All Teams Button */}
                            <button
                                onClick={() => setSelectedTeam('All')}
                                className={`w-full flex items-center px-3 py-2 text-sm font-semibold rounded-md transition-colors ${selectedTeam === 'All'
                                    ? 'bg-[#3d2b1f] border-y border-[#a87a2d]/20 text-[#facc15]'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`}
                            >
                                <span className="truncate">All Teams</span>
                                <span className="ml-auto text-xs text-gray-400 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700">
                                    {bookmarks.length}
                                </span>
                            </button>

                            {teams.map(team => (
                                <button
                                    key={team}
                                    onClick={() => setSelectedTeam(selectedTeam === team ? 'All' : team)}
                                    className={`w-full flex items-center px-3 py-2 text-sm font-semibold rounded-md transition-colors ${selectedTeam === team
                                        ? 'bg-[#3d2b1f] border-y border-[#a87a2d]/20 text-[#facc15]'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        }`}
                                >
                                    <span className="truncate">{team}</span>
                                </button>
                            ))}
                        </nav>
                    </div>
                </aside>

                {/* Main Content */}
                <main className="flex-1 p-4 md:p-8 overflow-y-auto">
                    <div className="max-w-6xl mx-auto">
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                                </h1>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                        {filteredBookmarks.length} resources{showFavoritesOnly ? ' (Favorites)' : ''}
                                    </span>
                                    {isSaving && (
                                        <span className="text-xs text-blue-500 animate-pulse flex items-center">
                                            <svg className="w-3 h-3 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Saving...
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                {/* View Toggle */}
                                <div className="flex p-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
                                    <button
                                        onClick={() => setViewMode('grid')}
                                        className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid'
                                            ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400'
                                            : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                                            }`}
                                        title="Grid view"
                                    >
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => setViewMode('list')}
                                        className={`p-1.5 rounded-md transition-colors ${viewMode === 'list'
                                            ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400'
                                            : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                                            }`}
                                        title="List view"
                                    >
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Theme Toggle */}
                                <button
                                    onClick={toggleTheme}
                                    className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                                >
                                    {theme === 'dark' ? (
                                        <svg className="w-5 h-5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {filteredBookmarks.length === 0 ? (
                            <div className="text-center py-20">
                                <div className="inline-flex items-center justify-center p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
                                    <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <p className="text-gray-500 dark:text-gray-400">No bookmarks found for this team.</p>
                            </div>
                        ) : (
                            <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" : "space-y-3"}>
                                {filteredBookmarks.map((bookmark, index) => (
                                    <div
                                        key={`${bookmark.name}-${index}`}
                                        className={`group relative bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-between ${viewMode === 'grid' ? 'p-6 flex-col items-start space-y-4 md:flex-row md:items-start md:space-y-0' : 'p-4'
                                            }`}
                                    >
                                        <div className="flex items-center gap-4 flex-1 min-w-0">
                                            {viewMode === 'list' && (
                                                <div className="flex-shrink-0">
                                                    {getIconForUrl(bookmark.url)}
                                                </div>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <a
                                                    href={bookmark.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-usage-handled="true"
                                                    onClick={() => {
                                                        logFileOpen({
                                                            fileId: bookmark.url,
                                                            fileName: bookmark.name,
                                                            fileUrl: bookmark.url,
                                                            source: 'Bookmarks',
                                                            action: 'open_link',
                                                            team: bookmark.team || '',
                                                            userEmail: auth.user?.email,
                                                            userName: auth.user?.name,
                                                        });
                                                    }}
                                                    className="block"
                                                >
                                                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-0.5 group-hover:text-blue-600 dark:group-hover:text-blue-400 truncate">
                                                        {bookmark.name}
                                                    </h3>
                                                </a>
                                                <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                                                    <span className="truncate">{getDomainFromUrl(bookmark.url)}</span>
                                                    <span className="px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700/50 text-[10px] font-medium uppercase tracking-wider">
                                                        {bookmark.team || 'General'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-4 ml-4">
                                            {viewMode === 'grid' && getIconForUrl(bookmark.url)}

                                            <button
                                                onClick={(e) => toggleFavorite(e, bookmark.url)}
                                                className="focus:outline-none transition-transform active:scale-95"
                                                title={favoriteUrls.has(bookmark.url) ? "Remove from favorites" : "Add to favorites"}
                                            >
                                                <svg
                                                    className={`w-5 h-5 ${favoriteUrls.has(bookmark.url) ? 'text-red-500 fill-current' : 'text-gray-300 dark:text-gray-600 hover:text-red-400'}`}
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                    fill={favoriteUrls.has(bookmark.url) ? 'currentColor' : 'none'}
                                                    strokeWidth={2}
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                                </svg>
                                            </button>

                                            <a
                                                href={bookmark.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-usage-handled="true"
                                                onClick={() => {
                                                    logFileOpen({
                                                        fileId: bookmark.url,
                                                        fileName: bookmark.name,
                                                        fileUrl: bookmark.url,
                                                        source: 'Bookmarks',
                                                        action: 'open_link',
                                                        team: bookmark.team || '',
                                                        userEmail: auth.user?.email,
                                                        userName: auth.user?.name,
                                                    });
                                                }}
                                                className="p-1.5 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-gray-400 hover:text-blue-500 transition-colors"
                                            >
                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                </svg>
                                            </a>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
}
